import { Button } from 'antd'
import PropTypes from 'prop-types'
import * as React from 'react'
import {
  memo,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useRef,
  useState,
} from 'react'
import SignatureCanvas from 'react-signature-canvas'

import { hasText } from '../utils/helpers'

type Props = {
  disabled: boolean
  readonly: boolean
  onChange: (value: Array<string | null | undefined>) => undefined
  onReset: () => undefined
  imageBase: string
  noImageText: string
  imageMarkup: string
  label: string | ReactNode
}

function ImageMarkerFC({
  disabled,
  readonly,
  onChange,
  imageBase,
  noImageText,
  imageMarkup,
  label,
}: Props) {
  const imageBaseRef = useRef<HTMLImageElement | null>(null)
  const sigCanvas = useRef<SignatureCanvas | null>(null)
  const [imageDimensions, setImageDimensions] = useState<{
    height: number | null
    width: number | null
  }>({
    height: null,
    width: null,
  })

  const onLoadImage = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.target as HTMLImageElement
    setImageDimensions({
      height: img.offsetHeight,
      width: img.offsetWidth,
    })
  }, [])

  React.useEffect(() => {
    function handleResize() {
      setImageDimensions({
        height: imageBaseRef.current?.offsetHeight || 0,
        width: imageBaseRef.current?.offsetWidth || 0,
      })
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  })

  const onEnd = useCallback(() => {
    if (disabled || readonly) return

    if (sigCanvas.current?.isEmpty()) {
      onChange([])
      return
    }

    const signature = sigCanvas.current?.getCanvas().toDataURL('image/png')
    onChange([signature])
  }, [disabled, onChange, readonly])

  const onClear = useCallback(() => {
    if (disabled || readonly) return

    sigCanvas.current?.clear()
    onChange([])
  }, [disabled, onChange, readonly])

  if (!imageBase) {
    return hasText(noImageText) ? (
      <p className="p-2 text-center text-xs text-txt">{noImageText}</p>
    ) : null
  }

  return (
    <div className="w-full overflow-hidden">
      <div className="flex w-full justify-center">
        <div className="relative rounded border">
          <img
            alt="Signature"
            className="rounded"
            onLoad={onLoadImage}
            ref={imageBaseRef}
            src={imageBase}
            style={{ maxHeight: '75vh' }}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
            }}
          >
            {disabled || readonly ? (
              hasText(imageMarkup) && (
                <img
                  alt={`${label}_markup`}
                  onLoad={onLoadImage}
                  src={imageMarkup}
                  style={{
                    width: imageDimensions.width || 200,
                    height: imageDimensions.height || 200,
                  }}
                />
              )
            ) : (
              <SignatureCanvas
                canvasProps={{
                  width: imageDimensions.width || 200,
                  height: imageDimensions.height || 200,
                }}
                onEnd={onEnd}
                penColor="red"
                ref={sigCanvas}
              />
            )}
          </div>
          {sigCanvas?.current?.isEmpty() !== true && !disabled && !readonly && (
            <Button
              className="absolute bottom-0 right-0 mb-2 mr-2 px-3 text-sm"
              onClick={onClear}
              type="link"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

ImageMarkerFC.defaultProps = {
  onChange: () => undefined,
  disabled: false,
}

ImageMarkerFC.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  imageMarkup: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

type AreEqualProps = Readonly<{
  disabled: boolean
  readonly: boolean
  onChange: (value: Array<string | null | undefined>) => undefined
  onReset: () => undefined
  imageBase: string
  noImageText: string
  imageMarkup: string
  label: string | ReactNode
}>

function areEqual(p: AreEqualProps, n: AreEqualProps) {
  return (
    p.disabled === n.disabled &&
    p.readonly === n.readonly &&
    p.onChange === n.onChange &&
    p.onReset === n.onReset &&
    p.imageBase === n.imageBase &&
    p.noImageText === n.imageBase &&
    p.imageMarkup === n.imageMarkup &&
    p.label === n.label
  )
}

export const ImageMarker = memo(ImageMarkerFC, areEqual)

export default ImageMarker
